import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ConfirmDeleteModal from '../components/shared/ConfirmDeleteModal';
import NowName from '../components/shared/NowName';
import { FormModal, Field, FilePicker, inputCls, btnGhost } from '../components/shared/FormKit';
import { RequestDetailShell, RequestHeader } from '../components/workflow/RequestDetailShell';
import StatusActionsCard from '../components/workflow/StatusActionsCard';
import ChangesSinceReturnCard from '../components/workflow/ChangesSinceReturnCard';
import WorkflowStatusBadge from '../components/workflow/WorkflowStatusBadge';
import { SectionCard, DetailGrid, DetailItem } from '../components/workflow/SectionCard';
import { detailLabel, detailValue } from '../components/workflow/constants';
import AttachmentsList from '../components/workflow/AttachmentsList';
import EditableAttachments, { useRemovableFiles } from '../components/workflow/EditableAttachments';
import HistoryCard from '../components/workflow/HistoryCard';
import RejectModal from '../components/workflow/RejectModal';
import ReturnCommentsBanner from '../components/workflow/ReturnCommentsBanner';
import FieldCommentAnchor from '../components/workflow/FieldCommentAnchor';
import { useFieldComments, FieldCommentsProvider, FieldCommentInline } from '../components/workflow/fieldComments';
import { FormValidationProvider, useFormValidation } from '../components/shared/formValidation';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import { fetchJSON, API_BASE_URL } from '../utils/api';

// Training absence declaration — detail page
// (docs/modules/workflow-ld-absence.md). Requester modifies (until approved);
// ld:absence:review holders approve/reject.
const CHANGE_LABELS = {
  sessionTitle: 'Session',
  sessionDate: 'Session Date',
  reasonCategory: 'Reason',
  reasonDetail: 'Details',
  // Nobody answers this — it follows the
  // attachments. Kept here so history entries that recorded it stay readable.
  evidence: 'Evidence Attached',
  remarks: 'Remarks',
  // 這條流程的附件區叫 Evidence，退件意見與 History 都該跟著這麼叫，
  // 而不是共用表的 'Attachments'。
  files: 'Evidence',
};

const fmtChangeValue = (field, v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  const s = String(v);
  return /^\d{4}-\d{2}-\d{2}(T|$)/.test(s) ? formatDate(s) : s;
};

const WorkflowAbsenceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [request, setRequest] = useState(null);
  const [existingRecord, setExistingRecord] = useState(null);
  const [attendanceRecord, setAttendanceRecord] = useState(null);
  const [canReview, setCanReview] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [modifying, setModifying] = useState(false);
  const [approving, setApproving] = useState(false);
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    try {
      const [detail, hist] = await Promise.all([
        fetchJSON(`/api/ld/absence-requests/${id}`),
        fetchJSON(`/api/ld/absence-requests/${id}/events`),
      ]);
      setRequest(detail.request);
      setExistingRecord(detail.existingRecord || null);
      setAttendanceRecord(detail.attendanceRecord || null);
      setCanReview(detail.canReview);
      setCanModify(detail.canModify);
      setCanDelete(detail.canDelete);
      setEvents(hist.data || []);
      setError(null);
    } catch (e) { setError(e.message); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const approve = async () => {
    setActing(true);
    setError(null);
    try {
      await fetchJSON(`/api/ld/absence-requests/${id}/approve`, { method: 'POST', body: JSON.stringify({}) });
      setApproving(false);
      setNotice('Declaration approved — the absence has been recorded and the requester notified.');
      await load();
    } catch (e) {
      setError(e.message);
      setApproving(false);
    }
    finally { setActing(false); }
  };

  const resubmit = async () => {
    setActing(true);
    setError(null);
    try {
      await fetchJSON(`/api/ld/absence-requests/${id}/resubmit`, { method: 'POST' });
      setNotice('Declaration re-submitted — reviewers have been notified.');
      await load();
    } catch (e) { setError(e.message); }
    finally { setActing(false); }
  };

  const hasAwaitingInfo = request && request.status === 'awaiting_info';
  const pending = request && request.status === 'requested';

  // 退件的逐項意見：能退件的人就能逐格留言，被退回的人就讀得到。
  const canComment = !!request && (canReview && pending);
  const fc = useFieldComments({
    events,
    canComment,
    returned: !!request && request.status === 'awaiting_info',
    labels: CHANGE_LABELS,
  });

  return (
    <RequestDetailShell
      backTo="/workflow/absence"
      backLabel="Training Absence"
      loading={!request}
      error={error}
      notice={notice}
    >
      {request && (
        <FieldCommentsProvider value={fc}>
          <RequestHeader
            requestNo={request.requestNo}
            typeLabel="Training Absence"
            badge={<WorkflowStatusBadge status={request.status} />}
            submittedBy={request.requesterEmail}
            submittedAt={request.createdAt}
          />

          {/* Both of these would make approval fail — say so before the click. */}
          {attendanceRecord && pending && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 flex items-start gap-2">
              <span>
                {request.staffNameNow || request.staffName} is recorded as having <span className="font-medium">attended</span> this session
                {attendanceRecord.trainingHours ? ` (${attendanceRecord.trainingHours} h)` : ''} — approving would
                contradict the attendance record, so reject this declaration unless the attendance is wrong.
              </span>
            </div>
          )}
          {existingRecord && pending && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 flex items-start gap-2">
              <span>
                An absence for this session is already on record
                {existingRecord.reasonCategory ? ` (${existingRecord.reasonCategory})` : ''}
                {existingRecord.sourceRequestNo ? ` from ${existingRecord.sourceRequestNo}` : ''} —
                approving would double-record it, so reject this declaration unless the record is wrong.
              </span>
            </div>
          )}

          <StatusActionsCard
            showDecider
            sides={[{
              key: 'main',
              label: null,
              status: request.status,
              note: request.statusUpdatedAt ? formatDateTime(request.statusUpdatedAt) : null,
            }]}
            busy={acting}
            events={events}
            onApprove={canReview && pending ? () => setApproving(true) : null}
            onReject={canComment ? () => setRejecting(true) : null}
            onModify={canModify && request.status !== 'approved' ? () => setModifying(true) : null}
            onResubmit={canModify && hasAwaitingInfo ? resubmit : null}
            onDelete={canDelete ? () => setConfirmingDelete(true) : null}
          >
            <div>
              <p className={detailLabel}>Session</p>
              <p className={detailValue}>{request.sessionTitle}</p>
              {request.status === 'approved' && (
                <p className="text-xs text-green-600 mt-1">Absence recorded on the training record</p>
              )}
            </div>
          </StatusActionsCard>

          <ChangesSinceReturnCard
            events={events}
            changeLabels={CHANGE_LABELS}
            fmtChangeValue={fmtChangeValue}
          />

          <SectionCard title="Declaration Details">
            <DetailGrid>
              <DetailItem label="Staff"><NowName value={request.staffNameNow || request.staffName} now={request.staffNameNow} /></DetailItem>
              <DetailItem label="Session" field="sessionTitle">{request.sessionTitle}</DetailItem>
              <DetailItem label="Session Date" field="sessionDate">{request.sessionDate ? formatDate(request.sessionDate) : '—'}</DetailItem>
              <DetailItem label="Reason" field="reasonCategory">{request.reasonCategory}</DetailItem>
              <DetailItem label="Details" field="reasonDetail">{request.reasonDetail || '—'}</DetailItem>
              {/* No "Has evidence" row — the Evidence
                  card below is the answer, and it cannot disagree with itself. */}
              <DetailItem label="Remarks" field="remarks">{request.remarks || '—'}</DetailItem>
            </DetailGrid>
          </SectionCard>

          <SectionCard title="Evidence">
            <AttachmentsList
              files={request.files || []}
              downloadHref={(f) => `${API_BASE_URL}/api/ld/absence-requests/${request.id}/files/${f.id}`}
              badgeFor={() => ['Evidence', 'bg-blue-50 text-blue-700']}
            />
            <FieldCommentAnchor field="files" label="Evidence" />
          </SectionCard>

          <HistoryCard
            events={events}
            actionLabels={{ record_created: ['Absence record created', 'bg-green-50 text-green-700'] }}
            changeLabels={CHANGE_LABELS}
            fmtChangeValue={fmtChangeValue}
          />

          {approving && (
            <FormModal
              title={`Approve ${request.requestNo}`}
              size="md"
              onClose={() => setApproving(false)}
              footer={(
                <>
                  <button type="button" onClick={() => setApproving(false)} className={btnGhost}>Cancel</button>
                  <button
                    type="button"
                    onClick={approve}
                    disabled={acting}
                    className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {acting ? 'Approving…' : 'Approve'}
                  </button>
                </>
              )}
            >
              <p className="text-sm text-slate-700">
                Approving records <span className="font-semibold">{request.staffNameNow || request.staffName}</span> as absent from{' '}
                <span className="font-semibold">{request.sessionTitle}</span>, with the stated reason. Any evidence
                attached here is copied onto the training record.
              </p>
              {(existingRecord || attendanceRecord) && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  {attendanceRecord
                    ? 'This staff member is recorded as having attended — approving will fail. Return the declaration for changes instead.'
                    : 'An absence is already on record — approving will fail. Return the declaration for changes instead.'}
                </div>
              )}
            </FormModal>
          )}
          {rejecting && (
            <RejectModal
              requestNo={request.requestNo}
              fieldComments={fc}
              onClose={() => setRejecting(false)}
              onSubmit={async (reason, fieldComments) => {
                await fetchJSON(`/api/ld/absence-requests/${request.id}/reject`, {
                  method: 'POST',
                  body: JSON.stringify({ reason, fieldComments }),
                });
                setRejecting(false);
                fc.clear();
                setNotice('Returned to the requester for changes.');
                load();
              }}
            />
          )}
          {modifying && (
            <AbsenceModifyModal
              request={request}
              events={events}
              onClose={() => setModifying(false)}
              onDone={() => {
                setModifying(false);
                setNotice(hasAwaitingInfo
                  ? 'Changes saved. Press Re-submit to send the declaration back for review.'
                  : 'Changes saved.');
                load();
              }}
            />
          )}
          {confirmingDelete && (
            <ConfirmDeleteModal
              title={`Delete ${request.requestNo}`}
              message="This permanently deletes the declaration and its evidence. This cannot be undone."
              onClose={() => setConfirmingDelete(false)}
              onConfirm={async () => {
                await fetchJSON(`/api/ld/absence-requests/${request.id}`, { method: 'DELETE' });
                navigate('/workflow/absence');
              }}
            />
          )}
        </FieldCommentsProvider>
      )}
    </RequestDetailShell>
  );
};

const AbsenceModifyModal = ({ request, events = [], onClose, onDone }) => {
  const [sessions, setSessions] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [otherLabel, setOtherLabel] = useState('Others');
  const [sessionId, setSessionId] = useState(
    request.sessionId != null ? String(request.sessionId) : ''
  );
  const [reasonCategory, setReasonCategory] = useState(request.reasonCategory || '');
  const [reasonDetail, setReasonDetail] = useState(request.reasonDetail || '');
  const [remarks, setRemarks] = useState(request.remarks || '');
  const [files, setFiles] = useState([]);
  const { removeIds, toggle, appendTo } = useRemovableFiles();
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    // The declared session and reason always stay selectable, whatever
    // /options returns — including when it fails outright (a super_admin whose
    // login is not on the staff master gets 404 STAFF_NOT_FOUND). An empty
    // dropdown on a field that already has a value reads as data loss.
    const withDeclared = (list) => {
      if (request.sessionId != null && !list.some(s => String(s.id) === String(request.sessionId))) {
        list.push({ id: request.sessionId, courseTitle: request.sessionTitle, date: request.sessionDate });
      }
      return list;
    };
    fetchJSON('/api/ld/absence-requests/options')
      .then((d) => {
        if (!active) return;
        const list = d.sessions || [];
        // /options is scoped to the caller, and only the requester (or
        // super_admin on a rescue) can modify — so for the normal case this is
        // exactly their remaining candidates, minus this declaration's own
        // session, which an open declaration always excludes.
        setSessions(withDeclared(list));
        const reasonList = d.reasons || [];
        // Same for a reason that has since been deactivated on the list.
        if (request.reasonCategory && !reasonList.includes(request.reasonCategory)) {
          reasonList.push(request.reasonCategory);
        }
        setReasons(reasonList);
        setOtherLabel(d.other || 'Others');
      })
      .catch(() => {
        if (!active) return;
        setSessions(withDeclared([]));
        setReasons(request.reasonCategory ? [request.reasonCategory] : []);
      });
    return () => { active = false; };
  }, [request.sessionId, request.sessionTitle, request.sessionDate, request.reasonCategory]);

  const needsDetail = reasonCategory === otherLabel;

  // The same gaps the submission form marks, on the same `field=` keys.
  const gaps = useMemo(() => {
    const g = [];
    if (!sessionId) g.push({ key: 'sessionTitle', label: 'Training Session' });
    if (!reasonCategory) g.push({ key: 'reasonCategory', label: 'Reason' });
    if (needsDetail && !reasonDetail.trim()) g.push({ key: 'reasonDetail', label: 'Details' });
    return g;
  }, [sessionId, reasonCategory, needsDetail, reasonDetail]);
  const validation = useFormValidation(gaps);

  const submit = async () => {
    if (!validation.validate()) { setError(null); return; }
    setSaving(true);
    setError(null);
    try {
      const form = new FormData();
      form.set('sessionId', sessionId);
      form.set('reasonCategory', reasonCategory);
      // Trimmed like the submission form, but ALWAYS sent: on PATCH an omitted
      // field means "unchanged", so an empty string is how an existing detail
      // or remark gets cleared (the server stores it as null).
      form.set('reasonDetail', reasonDetail.trim());
      form.set('remarks', remarks.trim());
      files.forEach(f => form.append('files', f));
      appendTo(form);
      await fetchJSON(`/api/ld/absence-requests/${request.id}`, { method: 'PATCH', body: form });
      onDone();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <FormModal
      title={`Modify ${request.requestNo}`}
      size="md"
      onClose={onClose}
      footer={(
        <>
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </>
      )}
    >
      <ReturnCommentsBanner events={events} />
      {(error || validation.error) && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error || validation.error}</div>
      )}
      <FormValidationProvider value={validation.providerValue}>
        <Field label="Training Session" field="sessionTitle" required>
          <select value={sessionId} onChange={e => setSessionId(e.target.value)} className={inputCls}>
            <option value="">Select…</option>
            {sessions.map(s => (
              <option key={s.id} value={s.id}>
                {s.courseTitle}{s.date ? ` — ${formatDate(s.date)}` : ''}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Reason" field="reasonCategory" required>
          <select value={reasonCategory} onChange={e => setReasonCategory(e.target.value)} className={inputCls}>
            {reasons.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
        <Field label="Details" field="reasonDetail" required={needsDetail} hint={needsDetail ? `Required when the reason is '${otherLabel}'.` : undefined}>
          <textarea rows="2" value={reasonDetail} onChange={e => setReasonDetail(e.target.value)} className={inputCls} />
        </Field>
        <Field label="Remarks" field="remarks">
          <textarea rows="2" value={remarks} onChange={e => setRemarks(e.target.value)} className={inputCls} />
        </Field>
        {/* Attaching the file is the whole answer — no
            "I have supporting evidence" tick-box. */}
        <EditableAttachments
          files={request.files || []}
          removeIds={removeIds}
          onToggle={toggle}
          downloadHref={(f) => `${API_BASE_URL}/api/ld/absence-requests/${request.id}/files/${f.id}`}
          badgeFor={() => ['Evidence', 'bg-blue-50 text-blue-700']}
          label="Current Evidence"
        />
        <FieldCommentInline field="files" />
        <Field label="Add Evidence">
          <FilePicker
            multiple
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            onChange={e => setFiles(Array.from(e.target.files || []))}
          />
        </Field>
      </FormValidationProvider>
    </FormModal>
  );
};

export default WorkflowAbsenceDetail;
